import React, { useState } from 'react';
import { FiChevronDown, FiChevronUp, FiShield, FiMail, FiGlobe, FiUser, FiFileText, FiSettings, FiRefreshCw } from 'react-icons/fi';
import styled, { keyframes } from 'styled-components';

// Animação personalizada
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Componentes estilizados
const PremiumContainer = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
  padding: 4rem 1rem;
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  animation: ${fadeIn} 0.6s ease-out;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  background: linear-gradient(90deg, #3b82f6, #6366f1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #3b82f6, #6366f1);
    border-radius: 2px;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const IntroCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  margin-bottom: 2.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.03);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 5px;
    height: 100%;
    background: linear-gradient(to bottom, #3b82f6, #6366f1);
  }
`;

const IntroText = styled.p`
  color: #334155;
  font-size: 1.05rem;
  line-height: 1.8;
  margin: 0;

  strong {
    color: #1e40af;
    font-weight: 600;
  }
`;

const AccordionItem = styled.div`
  background: white;
  border-radius: 12px;
  margin-bottom: 1.25rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  }
`;

const AccordionHeader = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #f8fafc;
  }
`;

const AccordionTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;

  svg {
    margin-right: 1rem;
    color: #3b82f6;
    flex-shrink: 0;
  }
`;

const AccordionContent = styled.div`
  padding: 0 2rem 1.5rem 4.5rem;
  color: #64748b;
  line-height: 1.8;
  border-top: 1px solid #f1f5f9;
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 4rem;
  color: #94a3b8;
  font-size: 0.9rem;

  p {
    margin: 0.5rem 0;
  }

  .highlight {
    color: #64748b;
    font-weight: 500;
  }
`;

const PoliticaPrivacidade = () => {
  const [activeSection, setActiveSection] = useState(null);

  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  const sections = [
    {
      id: 'coleta',
      title: "Coleta de Informações",
      icon: <FiUser size={20} />,
      content: "Coletamos informações como nome, e-mail, telefone, tipo de visto desejado, localização e preferências de realocação. Esses dados são essenciais para fornecer um serviço personalizado e eficiente."
    },
    {
      id: 'uso',
      title: "Uso das Informações",
      icon: <FiGlobe size={20} />,
      content: "Utilizamos seus dados para facilitar a conexão com empresas e consultores de realocação, enviar notificações relevantes e melhorar continuamente sua experiência. Seus dados nos ajudam a criar soluções sob medida para suas necessidades de realocação."
    },
    {
      id: 'compartilhamento',
      title: "Compartilhamento de Dados",
      icon: <FiMail size={20} />,
      content: "Seus dados podem ser compartilhados exclusivamente com empresas parceiras cuidadosamente selecionadas, prestadores de serviço especializados e autoridades legais, quando exigido por lei. Mantemos padrões rigorosos de confidencialidade em todos os compartilhamentos."
    },
    {
      id: 'seguranca',
      title: "Segurança de Dados",
      icon: <FiShield size={20} />,
      content: "Implementamos um sistema de segurança multicamadas que inclui criptografia de ponta a ponta, autenticação de dois fatores e monitoramento contínuo. Realizamos auditorias regulares para garantir a máxima proteção dos seus dados."
    },
    {
      id: 'direitos',
      title: "Seus Direitos",
      icon: <FiFileText size={20} />,
      content: "Você tem direitos completos sobre seus dados pessoais, incluindo acesso, correção, portabilidade e exclusão. Para exercer esses direitos ou esclarecer dúvidas, entre em contato com nosso Encarregado de Proteção de Dados através do email privacidade@realocafacil.com."
    },
    {
      id: 'cookies',
      title: "Tecnologias de Rastreamento",
      icon: <FiSettings size={20} />,
      content: "Utilizamos cookies essenciais e analíticos para otimizar performance e personalização. Nosso painel de preferências permite controle granular sobre quais tecnologias são ativadas, garantindo transparência e escolha."
    },
    {
      id: 'atualizacoes',
      title: "Atualizações da Política",
      icon: <FiRefreshCw size={20} />,
      content: "Esta política é revisada trimestralmente para refletir as melhores práticas e mudanças regulatórias. Alterações significativas serão comunicadas com destaque em nosso site e, quando aplicável, via notificação direta."
    }
  ];

  return (
    <PremiumContainer>
      <ContentWrapper>
        <HeaderSection>
          <Title>Política de Privacidade</Title>
          <Subtitle>Transparência e segurança no tratamento dos seus dados pessoais</Subtitle>
        </HeaderSection>

        <IntroCard>
          <IntroText>
            Na <strong>Realoca Fácil</strong>, sua privacidade é nossa prioridade. Esta política detalha como coletamos, usamos, protegemos e gerenciamos suas informações pessoais, refletindo nosso compromisso com os mais altos padrões de proteção de dados e conformidade com a LGPD.
          </IntroText>
        </IntroCard>

        <div>
          {sections.map((section) => (
            <AccordionItem key={section.id}>
              <AccordionHeader onClick={() => toggleSection(section.id)}>
                <AccordionTitle>
                  {section.icon}
                  {section.title}
                </AccordionTitle>
                {activeSection === section.id ? <FiChevronUp size={20} color="#3b82f6" /> : <FiChevronDown size={20} color="#3b82f6" />}
              </AccordionHeader>
              
              {activeSection === section.id && (
                <AccordionContent>
                  {section.content}
                </AccordionContent>
              )}
            </AccordionItem>
          ))}
        </div>

        <Footer>
          <p>Documento atualizado em: <span className="highlight">30 de maio de 2025</span></p>
          <p>© 2025 Realoca Fácil. Todos os direitos reservados.</p>
        </Footer>
      </ContentWrapper>
    </PremiumContainer>
  );
};

export default PoliticaPrivacidade;